/**
 * Tutorial step definitions for TileForge.
 *
 * Each tutorial is an array of { title, target, text } objects.
 * target = CSS selector for the spotlight highlight (null = center screen).
 */

// ─── Tutorial 1: Getting Started ─────────────────────────────────────

export const GETTING_STARTED_STEPS = [
  {
    title: 'Welcome to TileForge',
    target: null,
    text: "Let's build your first map! This tutorial takes about 2 minutes. You can use the arrow keys or click Next to navigate.",
  },
  {
    title: 'Your Tileset',
    target: '#palette-panel',
    text: "We've loaded Tiny Town (by Kenney) as your starter tileset. You can see all the available tiles in this palette. Click any tile to select it.",
  },
  {
    title: 'Select a Ground Tile',
    target: '#palette-panel',
    text: "Try clicking one of the green grass tiles in the top-left of the palette. This will be your painting tile. The selected tile shows a yellow highlight.",
  },
  {
    title: 'Paint on the Canvas',
    target: '#canvas-wrapper',
    text: "Now click or drag on the canvas to paint! The Pencil tool (B) is active by default. Try painting some grass across the map.",
  },
  {
    title: 'Try Different Tiles',
    target: '#palette-panel',
    text: "Go back to the palette and select a road tile, a building tile, or a tree. Paint a small scene — a house by a road with some trees.",
  },
  {
    title: 'Switch Tools',
    target: '#toolbar',
    text: "Your tools: Pencil (B) for painting, Rectangle (R) for filling areas, Fill (G) for flood fill, Eraser (E) to clear. Try the Rectangle tool to fill a large area with one tile.",
  },
  {
    title: 'Brush Size',
    target: '#brush-size-indicator',
    text: "Press [ and ] to change brush size. A 3×3 brush paints much faster than 1×1 for large areas. The indicator in the toolbar shows your current size.",
  },
  {
    title: 'Layers',
    target: '#layer-panel',
    text: "Your map has 3 layers: Ground (terrain), Objects (walls, furniture), and Overhead (rooftops, tree canopies). Switch layers to paint on different levels.",
  },
  {
    title: 'Undo & Save',
    target: '#menu-bar',
    text: "Made a mistake? Ctrl+Z to undo. When you're happy, Ctrl+S to save your map as a .tmj file (Tiled-compatible). You can also export as PNG or TMX.",
  },
  {
    title: 'More Tiles',
    target: null,
    text: "Want more tileset art? Go to File > Add Tileset to load any PNG spritesheet. Check out kenney.nl/assets for hundreds of free CC0 tilesets, or browse itch.io for premium packs.",
  },
  {
    title: 'Generators',
    target: null,
    text: "Try Generate > Generate Map for instant procedural maps — forests, dungeons, caves, villages, and more. Each one is different!",
  },
  {
    title: 'You\'re Ready!',
    target: null,
    text: "That's it! Press F1 anytime for help, or check Help > Keyboard Shortcuts for the full shortcut list. Happy mapping!",
  },
];

// ─── Tutorial 2: Importing Tilesets ──────────────────────────────────

export const IMPORT_TILESET_STEPS = [
  {
    title: 'Importing New Tilesets',
    target: null,
    text: "In this tutorial, you'll download a free tileset from the web and import it into TileForge. This is how you'll add any tileset — free or purchased.",
  },
  {
    title: 'Step 1: Find a Tileset',
    target: null,
    text: "Open a new browser tab and go to:\n\nkenney.nl/assets/tiny-dungeon\n\nThis is Kenney's Tiny Dungeon pack — free CC0 tiles for dungeon/RPG maps. Click the Download button (it's free, no account needed).",
  },
  {
    title: 'Step 2: Unzip the Download',
    target: null,
    text: "Open the downloaded .zip file. Inside you'll find a Tilemap folder with tilemap_packed.png — that's the spritesheet we need. Extract it somewhere you can find it (Desktop or Downloads).",
  },
  {
    title: 'Step 3: Add the Tileset',
    target: '#menu-bar',
    text: "In TileForge, go to File > Add Tileset (or press the 'Add Tileset' button in the palette panel). Browse to the tilemap_packed.png you just extracted.",
  },
  {
    title: 'Step 4: Check Tile Size',
    target: '#palette-panel',
    text: "The tileset should load in the palette. If the tiles look wrong (too big, misaligned), right-click the palette and choose 'Tileset Settings' to adjust the tile size. Tiny Dungeon uses 16×16 pixels with no margin.",
  },
  {
    title: 'Step 5: Start Painting',
    target: '#canvas-wrapper',
    text: "You can now switch between your tilesets using the dropdown at the top of the palette panel. Select a dungeon tile and start painting walls, floors, doors, and decorations.",
  },
  {
    title: 'Where to Find More Tilesets',
    target: null,
    text: "Great free sources:\n\n• kenney.nl/assets — Hundreds of CC0 packs (free)\n• opengameart.org — Community art (various licenses)\n• itch.io/game-assets — Free and premium packs\n\nWhen buying tilesets, look for 'PNG spritesheet' format. TileForge works with any PNG tileset image.",
  },
  {
    title: 'Pro Tips',
    target: null,
    text: "• Most tilesets are 16×16 or 32×32 pixels per tile\n• 'Packed' versions have no spacing (easier to load)\n• Match your map's tile size to your tileset's tile size\n• You can use multiple tilesets in one map\n• Right-click tiles to set collision shapes and animations",
  },
  {
    title: 'Done!',
    target: null,
    text: "You now know how to find, download, and import any tileset. The same process works for premium tilesets from itch.io or any other source. Happy mapping!",
  },
];

// ─── Tutorial Registry ───────────────────────────────────────────────

export const TUTORIALS = {
  'getting-started': {
    name: 'Getting Started',
    description: 'Build your first map (2 min)',
    steps: GETTING_STARTED_STEPS,
    storageKey: 'tileforge_tutorial_getting_started',
  },
  'import-tileset': {
    name: 'Importing Tilesets',
    description: 'Download and add new tile art',
    steps: IMPORT_TILESET_STEPS,
    storageKey: 'tileforge_tutorial_import_tileset',
  },
};
